import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  return BASE_URL + imagePath;
};

// Programs API
export const programsAPI = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/programs`, { params });
    return response.data;
  },

  getPlanInfo: async (userId) => {
    const response = await axios.get(`${API_URL}/programs/plan-info/${userId}`);
    return response.data;
  },
  
  getFeatured: async () => {
    const response = await axios.get(`${API_URL}/programs`, { params: { featured: true, status: 'open' } });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/programs/${id}`);
    return response.data;
  },
  
  getByCreator: async (userId) => {
    const response = await axios.get(`${API_URL}/programs/by-creator/${userId}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/programs`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/programs/${id}`, data);
    return response.data;
  },
  
  toggleFeatured: async (id) => {
    const response = await axios.put(`${API_URL}/programs/${id}/toggle-featured`);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/programs/${id}`);
    return response.data;
  }
};

// Applications API
export const applicationsAPI = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/applications`, { params });
    return response.data;
  },
  
  getByEmail: async (email) => {
    const response = await axios.get(`${API_URL}/applications`, { params: { email } });
    return response.data;
  },
  
  getByUserEmail: async (userEmail) => {
    const response = await axios.get(`${API_URL}/applications`, { params: { userEmail } });
    return response.data;
  },
  
  getByProgram: async (programId, requesterId = null) => {
    const params = { programId };
    if (requesterId) params.requester_id = requesterId;
    const response = await axios.get(`${API_URL}/applications`, { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/applications/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/applications`, data);
    return response.data;
  },
  
  createWithFile: async (data, file) => {
    const formData = new FormData();
    
    // Add all fields to formData
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    
    // Add file if present
    if (file) {
      formData.append('pitchDeck', file);
    }
    
    const response = await axios.post(`${API_URL}/applications/with-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/applications/${id}`, data);
    return response.data;
  },
  
  updateStatus: async (id, statusData) => {
    const response = await axios.put(`${API_URL}/applications/${id}/status`, statusData);
    return response.data;
  },
  
  delete: async (id, userEmail = null, isAdmin = false) => {
    const response = await axios.delete(`${API_URL}/applications/${id}`, {
      params: { userEmail, isAdmin }
    });
    return response.data;
  },

  exportExcel: async (programId, requesterId = null) => {
    const params = {};
    if (requesterId) params.requester_id = requesterId;
    const response = await axios.get(`${API_URL}/applications/export/${programId}`, {
      responseType: 'blob',
      params
    });
    return response.data;
  }
};

// Judges API
export const judgesAPI = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/judges`, { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/judges`, data);
    return response.data;
  }
};

// Metrics API
export const metricsAPI = {
  getDashboard: async () => {
    const response = await axios.get(`${API_URL}/metrics/dashboard`);
    return response.data;
  },
  
  getProgram: async (id) => {
    const response = await axios.get(`${API_URL}/metrics/program/${id}`);
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/users`, { params });
    return response.data;
  },
  
  getEvaluators: async () => {
    const response = await axios.get(`${API_URL}/users/evaluators`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/users/${id}`);
    return response.data;
  },
  
  getProfile: async (id) => {
    const response = await axios.get(`${API_URL}/users/${id}/profile`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/users`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/users/${id}`, data);
    return response.data;
  },
  
  updatePassword: async (id, data) => {
    const response = await axios.put(`${API_URL}/users/${id}/password`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/users/${id}`);
    return response.data;
  },

  getOrganizationPlans: async () => {
    const response = await axios.get(`${API_URL}/users/organizations/plans`);
    return response.data;
  },

  updatePlan: async (userId, plan) => {
    const response = await axios.put(`${API_URL}/users/${userId}/plan`, { plan });
    return response.data;
  }
};

// Payments API
export const paymentsAPI = {
  createOrder: async (data) => {
    const response = await axios.post(`${API_URL}/payments/create-order`, data);
    return response.data;
  },
  
  getStatus: async (orderId) => {
    const response = await axios.get(`${API_URL}/payments/status/${orderId}`);
    return response.data;
  },
  
  getUserSubscription: async (userId) => {
    const response = await axios.get(`${API_URL}/payments/user-subscription/${userId}`);
    return response.data;
  }
};

// Evaluators API
export const evaluatorsAPI = {
  // Assignments
  getAssignments: async (params = {}) => {
    const response = await axios.get(`${API_URL}/evaluators/assignments`, { params });
    return response.data;
  },
  
  createAssignment: async (data) => {
    const response = await axios.post(`${API_URL}/evaluators/assignments`, data);
    return response.data;
  },
  
  createBulkAssignments: async (data) => {
    const response = await axios.post(`${API_URL}/evaluators/assignments/bulk`, data);
    return response.data;
  },
  
  deleteAssignment: async (id) => {
    const response = await axios.delete(`${API_URL}/evaluators/assignments/${id}`);
    return response.data;
  },
  
  getOrphanedSummary: async () => {
    const response = await axios.get(`${API_URL}/evaluators/admin/orphaned-summary`);
    return response.data;
  },
  
  cleanupOrphaned: async () => {
    const response = await axios.delete(`${API_URL}/evaluators/admin/cleanup-orphaned`);
    return response.data;
  },
  
  // My assignments (for evaluator dashboard)
  getMyAssignments: async (evaluatorId) => {
    const response = await axios.get(`${API_URL}/evaluators/my-assignments/${evaluatorId}`);
    return response.data;
  },
  
  // Scores
  getScores: async (params = {}) => {
    const response = await axios.get(`${API_URL}/evaluators/scores`, { params });
    return response.data;
  },

  getDetailedScores: async (applicationId) => {
    const response = await axios.get(`${API_URL}/evaluators/scores/detailed/${applicationId}`);
    return response.data;
  },

  getFeedback: async (applicationId) => {
    const response = await axios.get(`${API_URL}/evaluators/feedback/${applicationId}`);
    return response.data;
  },
  
  submitScore: async (data) => {
    const response = await axios.post(`${API_URL}/evaluators/scores`, data);
    return response.data;
  },
  
  updateScore: async (id, data) => {
    const response = await axios.put(`${API_URL}/evaluators/scores/${id}`, data);
    return response.data;
  },
  
  // Scoring Criteria
  getProgramCriteria: async (programId) => {
    const response = await axios.get(`${API_URL}/evaluators/programs/${programId}/criteria`);
    return response.data;
  },
  
  updateProgramCriteria: async (programId, criteria) => {
    const response = await axios.put(`${API_URL}/evaluators/programs/${programId}/criteria`, criteria);
    return response.data;
  }
};

// Settings API
export const settingsAPI = {
  getEmailSettings: async () => {
    const response = await axios.get(`${API_URL}/settings/email`);
    return response.data;
  },
  
  updateEmailSettings: async (data) => {
    const response = await axios.put(`${API_URL}/settings/email`, data);
    return response.data;
  },
  
  sendTestEmail: async (recipientEmail) => {
    const response = await axios.post(`${API_URL}/notifications/test-email`, { recipient_email: recipientEmail });
    return response.data;
  },
  
  getEmailLogs: async (limit = 50) => {
    const response = await axios.get(`${API_URL}/notifications/logs`, { params: { limit } });
    return response.data;
  }
};

export const messagesAPI = {
  send: async (data) => {
    const response = await axios.post(`${API_URL}/messages`, data);
    return response.data;
  },
  getByProgram: async (programId) => {
    const response = await axios.get(`${API_URL}/messages/program/${programId}`);
    return response.data;
  },
  getInbox: async (params) => {
    const response = await axios.get(`${API_URL}/messages/inbox`, { params });
    return response.data;
  },
  markAsRead: async (messageId, params) => {
    const response = await axios.put(`${API_URL}/messages/inbox/${messageId}/read`, null, { params });
    return response.data;
  }
};

export const aiEvaluatorsAPI = {
  getProfiles: async () => {
    const response = await axios.get(`${API_URL}/ai-evaluators/profiles`);
    return response.data;
  },
  getProfile: async (slug) => {
    const response = await axios.get(`${API_URL}/ai-evaluators/profiles/${slug}`);
    return response.data;
  },
  evaluate: async (programId, profileSlugs, requesterId) => {
    const response = await axios.post(`${API_URL}/ai-evaluators/evaluate/${programId}`, {
      profile_slugs: profileSlugs,
      requester_id: requesterId,
    });
    return response.data;
  },
};

export const orgUsersAPI = {
  list: async () => {
    const response = await axios.get(`${API_URL}/org-users`);
    return response.data;
  },
  create: async (data) => {
    const response = await axios.post(`${API_URL}/org-users`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/org-users/${id}`, data);
    return response.data;
  },
  remove: async (id) => {
    const response = await axios.delete(`${API_URL}/org-users/${id}`);
    return response.data;
  },
};

